import { DbRef } from '@caresend/types';
import { initBooking, initPatient, getUnknownErrorMessage, nullishFilter, objectMap, arrayToObj, initElementTimestamp, asyncTimeout, objectSome } from '@caresend/utils';
import dayjs from 'dayjs';
import { AddPatientEntityType } from '../../AddPatient/model.js';
import { CSVOrderInputFields } from './model.js';
import { searchPatient } from '../../PatientPicker/components/PatientForm/components/searchPatient.js';
import 'firebase/auth';
import '../../../../database/firebaseConfig/cloudFunctions.js';
import '../../../../database/firebaseConfig/init.js';
import { getValueOnce } from '../../../../database/firebaseConfig/methods.js';
import '@sentry/vue';
import '@fortawesome/fontawesome-svg-core';
import '@fortawesome/sharp-regular-svg-icons';
import 'buefy';
import '../../../ui/Icon.vue.js';
import '../../../../functions/configuration/icons.js';
import '../../../../database/algoliaConfig/indices.js';
import 'mobile-device-detect';
import '../../../../functions/tracking/index.js';
import '../../../../router/helpers/initRouter.js';
import '@firebase/auth';
import 'browser-image-compression';
import 'firebase/storage';
import 'js-sha256';

/* eslint-disable no-restricted-syntax */
/* eslint-disable no-await-in-loop */
let storeContext;
const TIMEOUT = 150;
const log = async (message) => {
    storeContext.commit('orderFlow/APPEND_ORDER_CSV_IMPORT_LOG', message);
    await asyncTimeout(TIMEOUT);
};
const logHeader = async (message) => {
    storeContext.commit('orderFlow/APPEND_ORDER_CSV_IMPORT_LOG_HEADER', message);
    await asyncTimeout(TIMEOUT);
};
const completeLogTask = async () => {
    storeContext.commit('orderFlow/SET_CURRENT_CSV_IMPORT_LOG_STATUS', { status: 'success' });
    await asyncTimeout(TIMEOUT);
};
const logTaskError = async () => {
    storeContext.commit('orderFlow/SET_CURRENT_CSV_IMPORT_LOG_STATUS', { status: 'error' });
    await asyncTimeout(TIMEOUT);
};
const processCSVInput = async (arr, context) => {
    var _a, _b, _c, _d;
    storeContext = context;
    const [headerRow, ...dataRows] = arr;
    if (!headerRow) {
        log('Empty file');
        await logTaskError();
        throw new Error('Empty file');
    }
    await logHeader('Order initialization');
    // get all non-empty values from the draft order ID column
    const draftOrderIDs = dataRows.map((row) => row[headerRow.indexOf(CSVOrderInputFields.DRAFT_ORDER_ID)])
        .filter((id) => id);
    let draftOrderID;
    // if a draft order ID is provided, initialize the order from the draft order
    if (draftOrderIDs.length > 0 && draftOrderIDs[0]) {
        // check that all draft order IDs are identical
        const draftOrderIDsMatch = draftOrderIDs.every((id) => id === draftOrderIDs[0]);
        if (!draftOrderIDsMatch) {
            log('All provided draft order IDs must match');
            await logTaskError();
            throw new Error('Draft order IDs do not match');
        }
        [draftOrderID] = draftOrderIDs;
        if (!draftOrderID)
            return;
        await log(`Mounting draft order: ${draftOrderID}`);
        await logTaskError();
        throw new Error('Draft orders are not yet supported. Please make changes to the order manually.');
    }
    else {
        await log('No draft order ID. Initializing order from scratch');
        storeContext.commit('orderFlow/RESET_ALL_BOOKING_PROCEDURES');
        await completeLogTask();
        draftOrderID = (_a = storeContext.state.draftOrder) === null || _a === void 0 ? void 0 : _a.id;
    }
    if (!draftOrderID) {
        log('Failed to initialize order');
        await logTaskError();
        throw new Error('Failed to initialize order');
    }
    // get all values from the initiating office id column
    const initiatingOfficeIDs = dataRows.map((row) => row[headerRow.indexOf(CSVOrderInputFields.INITIATING_OFFICE_ID)])
        .filter((id) => id);
    if (initiatingOfficeIDs.length > 0 && initiatingOfficeIDs[0]) {
        // check that all initiating office IDs are identical
        const initiatingOfficeIDsMatch = initiatingOfficeIDs.every((id) => id === initiatingOfficeIDs[0]);
        if (!initiatingOfficeIDsMatch) {
            log('All provided initiating office IDs must match');
            await logTaskError();
            throw new Error('Initiating office IDs do not match');
        }
        const initiatingOfficeID = initiatingOfficeIDs[0];
        await log(`Setting initiating office: ${initiatingOfficeID}`);
        storeContext.commit('orderFlow/SET_CREATED_BY_OFFICE_ID', initiatingOfficeID);
        await completeLogTask();
    }
    // get all values from the medical office id column
    const medicalOfficeIDs = dataRows.map((row) => row[headerRow.indexOf(CSVOrderInputFields.MEDICAL_OFFICE_ID)])
        .filter((id) => id);
    let medicalOfficeID;
    if (medicalOfficeIDs.length > 0 && medicalOfficeIDs[0]) {
        // check that all medical office IDs are identical
        const medicalOfficeIDsMatch = medicalOfficeIDs.every((id) => id === medicalOfficeIDs[0]);
        if (!medicalOfficeIDsMatch) {
            log('All provided medical office IDs must match');
            await logTaskError();
            throw new Error('Medical office IDs do not match');
        }
        [medicalOfficeID] = medicalOfficeIDs;
        if (!medicalOfficeID)
            return;
        await log(`Setting medical office: ${medicalOfficeID}`);
        storeContext.commit('orderFlow/SET_ORDER_OFFICE', { officeID: medicalOfficeID });
        await completeLogTask();
    }
    // get all values from the prescriber id column
    const prescriberIDs = dataRows.map((row) => row[headerRow.indexOf(CSVOrderInputFields.PRESCRIBER_ID)])
        .filter((id) => id);
    if (prescriberIDs.length > 0 && prescriberIDs[0]) {
        // check that all prescriber IDs are identical
        const prescriberIDsMatch = prescriberIDs.every((id) => id === prescriberIDs[0]);
        if (!prescriberIDsMatch) {
            log('All provided prescriber IDs must match');
            await logTaskError();
            throw new Error('Prescriber IDs do not match');
        }
        const prescriberID = prescriberIDs[0];
        await log(`Setting prescriber: ${prescriberID}`);
        storeContext.commit('orderFlow/SET_PRESCRIBER_ID_ON_ORDER', prescriberID);
        await completeLogTask();
    }
    for (const [index, row] of dataRows.entries()) {
        try {
            const patientFirstName = row[headerRow.indexOf(CSVOrderInputFields.PATIENT_FIRST_NAME)];
            const patientLastName = row[headerRow.indexOf(CSVOrderInputFields.PATIENT_LAST_NAME)];
            const patientMiddleInitial = row[headerRow.indexOf(CSVOrderInputFields.PATIENT_MIDDLE_INITIAL)];
            const patientDOB = row[headerRow.indexOf(CSVOrderInputFields.PATIENT_DOB)];
            const patientSex = row[headerRow.indexOf(CSVOrderInputFields.PATIENT_SEX)];
            const patientPhone = row[headerRow.indexOf(CSVOrderInputFields.PATIENT_PHONE)];
            const patientEmail = row[headerRow.indexOf(CSVOrderInputFields.PATIENT_EMAIL)];
            await logHeader(`Patient ${patientFirstName} ${patientLastName} (row ${index + 2})`);
            let missingFields = '';
            if (!patientFirstName)
                missingFields += 'First name, ';
            if (!patientLastName)
                missingFields += 'Last name, ';
            if (!patientDOB)
                missingFields += 'Date of birth, ';
            if (!patientSex)
                missingFields += 'Sex, ';
            if (!patientFirstName || !patientLastName || !patientDOB || !patientSex) {
                log(`Patient is missing required fields: ${missingFields}`);
                await logTaskError();
                throw new Error('Patient is missing required fields');
            }
            await log(`Searching for patient ${patientFirstName} ${patientLastName}`);
            const user = await searchPatient({
                patientFirstName,
                patientLastName,
                patientDob: dayjs(patientDOB).tz('UTC').startOf('D').valueOf(),
                patientGender: patientSex,
                userInfo: {
                    email: patientEmail !== null && patientEmail !== void 0 ? patientEmail : '',
                    phone: {
                        callingCode: '+1',
                        number: patientPhone !== null && patientPhone !== void 0 ? patientPhone : '',
                    },
                },
                props: {
                    entityType: AddPatientEntityType.PATIENT,
                },
            });
            await completeLogTask();
            let bookingID;
            try {
                if (user) {
                    /** User was found in system */
                    log(`Patient ${patientFirstName} ${patientLastName} found: ${user.id}`);
                    await completeLogTask();
                    // find any existing draft bookings with the patient ID
                    bookingID = (_c = Object.values((_b = storeContext.state.draftBookings) !== null && _b !== void 0 ? _b : {})
                        .find((booking) => booking.patientID === user.id)) === null || _c === void 0 ? void 0 : _c.id;
                    if (bookingID) {
                        log(`Found existing booking for ${patientFirstName} ${patientLastName}: ${bookingID}`);
                        await completeLogTask();
                    }
                    else {
                        await log('Adding patient to order');
                        const booking = initBooking(draftOrderID);
                        bookingID = booking.id;
                        storeContext.commit('orderFlow/ADD_PATIENT_BOOKING', booking);
                        if (medicalOfficeID) {
                            await storeContext.dispatch('offices/addPatientToOffice', {
                                officeID: medicalOfficeID,
                                patient: user,
                            });
                        }
                        await completeLogTask();
                    }
                    storeContext.commit('orderFlow/SET_PATIENT_ON_BOOKING', { patient: user, bookingID });
                }
                else {
                    /** User not found in system */
                    await log('Patient not found. Creating account and adding to office');
                    const patient = initPatient();
                    patient.info.firstName = patientFirstName;
                    patient.info.middleNames = patientMiddleInitial;
                    patient.info.lastName = patientLastName;
                    patient.info.dateOfBirth = dayjs(patientDOB).tz('UTC').startOf('D').valueOf();
                    patient.info.gender = patientSex;
                    if (patientEmail)
                        patient.info.email = patientEmail;
                    if (patientPhone) {
                        patient.info.phone = {
                            countryCode: 'US',
                            callingCode: '+1',
                            number: patientPhone,
                        };
                    }
                    /**
                     * Typecast required for optional action, this action will always exist
                     * in the store context for dashboard users
                     */
                    let userID;
                    try {
                        userID = await storeContext.dispatch('users/createPatientAndAddToOffice', {
                            draftUser: patient,
                            officeID: medicalOfficeID,
                        });
                    }
                    catch (_e) {
                        throw new Error(`User with this email already exists: ${patientEmail}`);
                    }
                    if (!userID) {
                        throw new Error('Failed to create patient');
                    }
                    await completeLogTask();
                    await log('Adding patient to order');
                    const booking = initBooking(draftOrderID);
                    storeContext.commit('orderFlow/ADD_PATIENT_BOOKING', booking);
                    bookingID = booking.id;
                    await completeLogTask();
                    if (!bookingID) {
                        throw new Error('Failed to add patient to order');
                    }
                    const newUser = await storeContext.getters['users/getUserByID'](userID);
                    if (!newUser) {
                        throw new Error('Failed to find created patient');
                    }
                    storeContext.commit('orderFlow/SET_PATIENT_ON_BOOKING', { patient: newUser, bookingID });
                }
            }
            catch (error) {
                log(`Error: ${getUnknownErrorMessage(error)}`);
                logTaskError();
                if (!bookingID) {
                    const booking = initBooking(draftOrderID);
                    storeContext.commit('orderFlow/ADD_PATIENT_BOOKING', booking);
                    bookingID = booking.id;
                }
            }
            /** Procedure related tasks */
            const procedure = await storeContext.dispatch('orderFlow/initProcedure', { bookingID });
            const procedureID = procedure.id;
            const icd10Codes = row[headerRow.indexOf(CSVOrderInputFields.ICD_10_CODES)];
            if (icd10Codes) {
                const codes = icd10Codes.split(' ');
                const codeOptions = [];
                for (const code of codes) {
                    await log(`Searching for ICD-10 code ${code}`);
                    const results = await storeContext.dispatch('orderFlow/searchAlgoliaICD10Codes', code);
                    await completeLogTask();
                    if (results.length && results[0]) {
                        log(`ICD-10 code ${code} ${results[0].name} found`);
                        await completeLogTask();
                        codeOptions.push({
                            id: results[0].id,
                            label: `${results[0].code} ${results[0].name}`,
                        });
                    }
                }
                await log('Encrypting and setting ICD-10 codes');
                await storeContext.dispatch('orderFlow/setDiagnosesOnProcedure', {
                    procedureID,
                    diagnoses: codeOptions,
                });
                await completeLogTask();
            }
            else {
                log('No ICD-10 codes provided. Skipped');
                await completeLogTask();
            }
            const placeAccountID = row[headerRow.indexOf(CSVOrderInputFields.PLACE_ACCOUNT_ID)];
            if (placeAccountID) {
                await log(`Setting lab account ID: ${placeAccountID}`);
                const placeAccount = await getValueOnce(`${DbRef.PLACE_ACCOUNTS}/${placeAccountID}`);
                if (!placeAccount) {
                    await logTaskError();
                }
                else {
                    storeContext.commit('orderFlow/SET_PLACE_ACCOUNT', {
                        procedureID,
                        placeAccount,
                    });
                    await completeLogTask();
                }
            }
            else {
                log('No lab account ID provided. Skipped');
                await completeLogTask();
            }
            // get all values from the product id column
            const productIDsEntry = row[headerRow.indexOf(CSVOrderInputFields.PRODUCT_ID)];
            if (productIDsEntry) {
                const productIDs = (_d = productIDsEntry === null || productIDsEntry === void 0 ? void 0 : productIDsEntry.split(' ')) !== null && _d !== void 0 ? _d : [];
                const products = (await Promise.all(productIDs.map((productID) => getValueOnce(`${DbRef.PRODUCTS}/${productID}`)))).filter(nullishFilter);
                await log(`Adding products: ${products.map((p) => p.name).join(', ')}`);
                const productElements = objectMap(arrayToObj(products, 'id'), (product) => (initElementTimestamp(product.id)));
                storeContext.commit('orderFlow/SET_PRODUCTS', {
                    procedureID,
                    newProducts: productElements,
                });
                await completeLogTask();
            }
            else {
                log('No product IDs provided. Skipped');
                await completeLogTask();
            }
        }
        catch (error) {
            log(`Error: ${getUnknownErrorMessage(error)}`);
            await logTaskError();
        }
    }
    await logHeader('Final steps');
    await log('Cleaning up order');
    const bookings = storeContext.state.draftBookings;
    const procedures = storeContext.state.draftProcedures;
    const bookingHasNonemptyProcedure = (booking) => {
        var _a;
        return objectSome((_a = booking.procedures) !== null && _a !== void 0 ? _a : {}, (procedureID) => {
            var _a, _b, _c, _d, _f, _g;
            return !!((_b = (_a = procedures === null || procedures === void 0 ? void 0 : procedures[procedureID.id]) === null || _a === void 0 ? void 0 : _a.diagnoses) === null || _b === void 0 ? void 0 : _b.length)
                || !!Object.values((_d = (_c = procedures === null || procedures === void 0 ? void 0 : procedures[procedureID.id]) === null || _c === void 0 ? void 0 : _c.placeAccounts) !== null && _d !== void 0 ? _d : {}).length
                || !!Object.values((_g = (_f = procedures === null || procedures === void 0 ? void 0 : procedures[procedureID.id]) === null || _f === void 0 ? void 0 : _f.products) !== null && _g !== void 0 ? _g : {}).length;
        });
    };
    const invalidBookings = Object.values(bookings !== null && bookings !== void 0 ? bookings : {}).filter((booking) => !booking.patientID && !bookingHasNonemptyProcedure(booking));
    for (const booking of invalidBookings) {
        storeContext.commit('orderFlow/REMOVE_PATIENT_BOOKING', booking.id);
    }
    await completeLogTask();
};

export { processCSVInput };
